import React, { useMemo } from 'react';

/**
 * Minimal, safe renderer for the light markdown models naturally emit.
 *
 * Supports bold, italic, inline code, links and bullet/numbered lists. Everything is
 * built as React elements — never `dangerouslySetInnerHTML` — because this renders
 * model output, which must be treated as untrusted.
 */

const LINK = /\[([^\]]+)\]\(([^)\s]+)\)/;
const BOLD = /\*\*([^*]+)\*\*/;
const ITALIC = /(?<!\*)\*([^*]+)\*(?!\*)/;
const CODE = /`([^`]+)`/;

/** Only in-app and plain web links are rendered; anything else stays as text. */
const isSafeHref = (href: string): boolean =>
  href.startsWith('/') || href.startsWith('https://') || href.startsWith('http://');

const renderInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let rest = text;
  let index = 0;

  while (rest.length) {
    const candidates = [
      { type: 'link' as const, match: LINK.exec(rest) },
      { type: 'bold' as const, match: BOLD.exec(rest) },
      { type: 'italic' as const, match: ITALIC.exec(rest) },
      { type: 'code' as const, match: CODE.exec(rest) },
    ].filter((c) => c.match) as { type: string; match: RegExpExecArray }[];

    if (!candidates.length) {
      nodes.push(rest);
      break;
    }

    // Whichever marker appears first wins, so nesting cannot mis-slice the string.
    const next = candidates.reduce((a, b) => (a.match.index <= b.match.index ? a : b));
    const { match, type } = next;

    if (match.index > 0) nodes.push(rest.slice(0, match.index));

    const key = `${keyPrefix}-${index++}`;
    if (type === 'link') {
      const [, label, href] = match;
      nodes.push(
        isSafeHref(href!) ? (
          <a
            key={key}
            href={href}
            className="font-medium underline underline-offset-2 hover:opacity-80"
            style={{ color: '#64B5F6' }}
            {...(href!.startsWith('/') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
          >
            {label}
          </a>
        ) : (
          <span key={key}>{label}</span>
        ),
      );
    } else if (type === 'bold') {
      nodes.push(<strong key={key} className="font-semibold">{match[1]}</strong>);
    } else if (type === 'italic') {
      nodes.push(<em key={key}>{match[1]}</em>);
    } else {
      nodes.push(
        <code key={key} className="rounded bg-black/[0.06] px-1 py-0.5 text-[0.9em]">
          {match[1]}
        </code>,
      );
    }

    rest = rest.slice(match.index + match[0].length);
  }

  return nodes;
};

const RichText: React.FC<{ text: string }> = ({ text }) => {
  const blocks = useMemo(() => {
    const lines = text.split('\n');
    const out: React.ReactNode[] = [];
    let list: { ordered: boolean; items: string[] } | null = null;

    const flushList = (key: string) => {
      if (!list) return;
      const { ordered, items } = list;
      const ListTag = ordered ? 'ol' : 'ul';
      out.push(
        <ListTag
          key={key}
          className={`my-1 space-y-0.5 pl-4 ${ordered ? 'list-decimal' : 'list-disc'}`}
        >
          {items.map((item, i) => (
            <li key={i}>{renderInline(item, `${key}-${i}`)}</li>
          ))}
        </ListTag>,
      );
      list = null;
    };

    lines.forEach((line, i) => {
      const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
      const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);

      if (bullet || numbered) {
        const ordered = Boolean(numbered);
        const content = (bullet ?? numbered)![1]!;
        if (!list || list.ordered !== ordered) flushList(`list-${i}`);
        list = list ?? { ordered, items: [] };
        list.items.push(content);
        return;
      }

      flushList(`list-${i}`);
      if (!line.trim()) return;
      out.push(
        <p key={`p-${i}`} className="whitespace-pre-wrap">
          {renderInline(line, `p-${i}`)}
        </p>,
      );
    });

    flushList('list-end');
    return out;
  }, [text]);

  return <div className="space-y-1.5">{blocks}</div>;
};

export default React.memo(RichText);
